import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Check, Zap, Users, Sparkles, Loader2, PiggyBank, Gift } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { PricingTiers } from "@/pages/marketing/Pricing";
import { PLAN_META } from "@/lib/planFeatures";
import { RedeemCodeInline } from "@/components/RedeemCodeInline";
import { BillingHistory } from "@/components/BillingHistory";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const PLAN_BADGE = {
  starter: "bg-slate-100 text-slate-700",
  growth: "bg-brand/10 text-brand-ink",
  scale: "bg-secondary text-foreground",
  fund_manager: "bg-primary text-primary-foreground",
};

function AnnualNudge({ plan, onChanged }) {
  const meta = PLAN_META[plan];
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  if (!meta?.yearly) return null;
  const savings = meta.monthly * 12 - meta.yearly;
  const switchNow = async () => {
    setBusy(true);
    try {
      await api.post("/payments/switch-to-annual");
      toast.success(`You're on annual billing - saving $${savings.toLocaleString()} a year`);
      setConfirm(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not switch to annual"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-brand/30 bg-brand/[0.06] p-5" data-testid="annual-nudge-card">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand"><PiggyBank className="h-5 w-5 text-brand-foreground" /></div>
        <div>
          <div className="font-semibold text-foreground">Save ${savings.toLocaleString()}/year with annual billing</div>
          <p className="text-sm text-muted-foreground">Pay ${meta.yearly.toLocaleString()}/yr instead of ${(meta.monthly * 12).toLocaleString()} - same {meta.name} plan, two months free. Prorated instantly, no interruption.</p>
        </div>
      </div>
      <Button onClick={() => setConfirm(true)} disabled={busy} className="gap-2" data-testid="switch-annual-btn">Switch to annual</Button>
      <AlertDialog open={confirm} onOpenChange={(value) => !busy && setConfirm(value)}>
        <AlertDialogContent data-testid="annual-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="annual-confirm-title">Switch to annual billing?</AlertDialogTitle>
            <AlertDialogDescription data-testid="annual-confirm-description">Your annual price is ${meta.yearly.toLocaleString()}. Stripe will charge your saved payment method now, crediting unused time on your current plan. Your next renewal will be in one year.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="annual-confirm-cancel">Keep monthly</AlertDialogCancel>
            <Button onClick={switchNow} disabled={busy} data-testid="annual-confirm-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm annual billing</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function UsageBar({ icon: Icon, label, used, limit }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div data-testid={`usage-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="mb-1.5 flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 font-medium text-slate-600"><Icon className="h-4 w-4 text-slate-400" /> {label}</span>
        <span className="font-semibold text-slate-900">{used}{limit === null ? "" : ` / ${limit}`}{limit === null ? " · unlimited" : ""}</span>
      </div>
      {limit !== null && (
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full ${pct >= 90 ? "bg-red-500" : "bg-brand"}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

const CANCEL_REASONS = [
  ["too_expensive", "Too expensive"],
  ["missing_features", "Missing features I need"],
  ["switching", "Switching to another tool"],
  ["not_raising", "Not fundraising right now"],
  ["closing", "Company winding down"],
  ["other", "Other"],
];

function SubscriptionControls({ sub, planName, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("feedback");
  const [offer, setOffer] = useState(null);
  if (!sub?.has_subscription || !["active", "trialing", "past_due"].includes(sub.status)) return null;
  const endDate = sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "the end of your billing period";

  const resume = async () => {
    setBusy(true);
    try { await api.post("/payments/resume"); toast.success("Welcome back - your plan will continue"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };

  const openCancel = async () => {
    let next = "feedback";
    try {
      const { data } = await api.get("/payments/save-offer");
      setOffer(data);
      if (data.eligible) next = "offer";
    } catch { /* fall through to feedback */ }
    setStep(next);
    setOpen(true);
  };

  const acceptOffer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/save-offer/accept");
      toast.success(`Done - ${data.percent_off}% off applied for the next ${data.months} months`);
      setOpen(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply the discount"); }
    finally { setBusy(false); }
  };

  const declineOffer = async () => {
    setBusy(true);
    try { await api.post("/payments/save-offer/decline"); }
    catch { /* offer stays available; still let them proceed to cancel */ }
    finally { setBusy(false); }
    setStep("feedback");
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post("/payments/cancel", { reason: reason || null, comment: comment.trim() || null });
      toast.success("Plan will cancel at the end of the billing period");
      setReason(""); setComment("");
      setOpen(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };

  if (sub.cancel_at_period_end) {
    return (
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="cancellation-notice">
        <p className="text-sm text-amber-900">
          Your <span className="font-semibold capitalize">{planName}</span> plan is set to cancel on <span className="font-semibold">{endDate}</span>. You'll keep full access until then, and move to the free Starter plan after.
        </p>
        <Button variant="outline" size="sm" onClick={resume} disabled={busy} data-testid="resume-plan-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resume plan"}
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
      <p className="text-xs text-muted-foreground" data-testid="subscription-renewal-date">
        {sub.current_period_end ? `Renews ${endDate}.` : "Active subscription."} Cancel anytime - you keep access until the period ends.
      </p>
      <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={openCancel} data-testid="cancel-plan-btn">Cancel plan</Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="cancel-plan-dialog">
          {step === "offer" ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Before you go - here's {offer?.percent_off ?? 50}% off</AlertDialogTitle>
                <AlertDialogDescription>
                  We'd hate to lose you. Stay on {planName} and we'll take {offer?.percent_off ?? 50}% off your next {offer?.months ?? 3} months, applied to your subscription instantly. This is a one-time offer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="rounded-lg border border-brand/30 bg-brand/10 p-4 text-center" data-testid="save-offer-step">
                <div className="h-display text-3xl font-bold text-brand-ink">{offer?.percent_off ?? 50}% off</div>
                <div className="text-xs font-medium text-foreground">for the next {offer?.months ?? 3} months</div>
              </div>
              <AlertDialogFooter>
                <Button variant="ghost" size="sm" onClick={declineOffer} disabled={busy} data-testid="save-offer-decline">No thanks, continue to cancel</Button>
                <Button onClick={acceptOffer} disabled={busy} data-testid="save-offer-accept">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Claim ${offer?.percent_off ?? 50}% off & stay`}
                </Button>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel your {planName} plan?</AlertDialogTitle>
                <AlertDialogDescription>
                  You'll keep everything until {endDate}, then move to the free Starter plan. Your investors, updates and documents stay - only paid features pause. You can resume anytime before then.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-3">
                <p className="text-sm font-medium text-foreground">Mind telling us why? (optional)</p>
                <RadioGroup value={reason} onValueChange={setReason} className="gap-1.5" data-testid="cancel-reason-group">
                  {CANCEL_REASONS.map(([k, l]) => (
                    <label key={k} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                      <RadioGroupItem value={k} data-testid={`cancel-reason-${k}`} /> {l}
                    </label>
                  ))}
                </RadioGroup>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Anything else we should know?" rows={2} className="text-sm" data-testid="cancel-comment" />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy} data-testid="keep-plan-btn">Keep my plan</AlertDialogCancel>
                <Button variant="destructive" onClick={doCancel} disabled={busy} data-testid="confirm-cancel-plan">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Cancel plan
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Billing() {
  const { company, user, reload } = useAuth();
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const [billing, setBilling] = useState(null);
  const [sub, setSub] = useState(null);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState("");
  const [historyError, setHistoryError] = useState("");
  const [loading, setLoading] = useState(true);
  const companyId = company?.id;
  const isOwner = company?.founder_user_id === user?.id;

  const loadHistory = useCallback(async () => {
    setHistoryError("");
    try { const { data } = await api.get("/payments/history"); setHistory(data); }
    catch (e) { setHistoryError(e?.response?.data?.detail || "Could not load billing history."); }
  }, []);
  const load = useCallback(async () => {
    if (!companyId || !isOwner) { setLoading(false); return; }
    setError(""); setLoading(true);
    try {
      const subscription = await api.get("/payments/subscription");
      const current = await api.get(`/companies/${companyId}/billing`);
      setSub(subscription.data); setBilling(current.data);
      await reloadRef.current();
    } catch (e) { setError(e?.response?.data?.detail || "Could not load your billing details."); }
    finally { setLoading(false); }
    await loadHistory();
  }, [companyId, isOwner, loadHistory]);
  useEffect(() => { load(); }, [load]);
  const changed = load;

  const cfg = billing?.config;

  return (
    <AppShell title="Billing & Plan" subtitle="Subscription & invoices">
      {!isOwner ? <p data-testid="billing-owner-only" className="py-6 text-sm text-muted-foreground">Only the workspace owner can manage billing.</p> : <>
      {loading && <p data-testid="billing-loading" className="py-4 text-sm text-muted-foreground">Loading your subscription…</p>}
      {error && <div role="alert" data-testid="billing-load-error" className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error} <Button onClick={load} variant="link" data-testid="billing-load-retry">Retry</Button></div>}
      {sub && ["past_due", "unpaid", "incomplete", "paused"].includes(sub.status) && <div role="alert" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="billing-payment-warning">Your subscription needs a payment update. {sub.status === "past_due" ? "Access continues while Stripe retries your payment." : "Paid features are paused until payment is resolved."} Open Manage billing below to review your invoice and payment method.</div>}
      {billing && (
        <Card className="mb-8 overflow-hidden rounded-xl border bg-card p-6" data-testid="current-plan-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900"><CreditCard className="h-5 w-5 text-white" /></div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="h-display text-xl font-semibold text-foreground" data-testid="billing-current-plan-name">{cfg?.name} plan</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PLAN_BADGE[billing.plan] || PLAN_BADGE.starter}`} data-testid="plan-status-badge">{sub?.cancel_at_period_end ? "canceling" : billing.plan_status === "comped" ? "complimentary" : billing.plan_status}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{billing.plan_status === "comped" ? "Complimentary access from the TenX team - no card, no expiry." : cfg?.tagline}</p>
                </div>
              </div>
            </div>
            <div className="text-right">
              {billing.plan_status === "comped" ? (
                <div className="h-display text-2xl font-bold text-emerald-600" data-testid="plan-price-label">Free<span className="text-sm font-medium text-muted-foreground"> · on us</span></div>
              ) : (
                <div className="h-display text-2xl font-bold text-primary" data-testid="plan-price-label">
                  {sub?.interval === "year" && cfg?.price_yearly
                    ? <>{`$${cfg.price_yearly.toLocaleString()}`}<span className="text-sm font-medium text-muted-foreground">/yr</span></>
                    : <>{cfg?.price_monthly ? `$${cfg.price_monthly}` : "$0"}<span className="text-sm font-medium text-muted-foreground">/mo</span></>}
                </div>
              )}
              {sub?.interval === "year" && billing.plan_status !== "comped" && <div className="text-[11px] font-medium text-emerald-600">Annual billing - 2 months free</div>}
              {sub?.has_subscription && <div className="mt-1 text-xs text-muted-foreground" data-testid="billing-price-disclaimer">Plan price before discounts and credits</div>}
            </div>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <UsageBar icon={Users} label="Investors" used={billing.investor_count} limit={cfg?.investors} />
            <UsageBar icon={Sparkles} label="AI updates this month" used={billing.ai_updates_used} limit={cfg?.ai_updates_per_month} />
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 border-t pt-5 text-sm">
            {[["Document Builder & e-signatures", cfg?.document_builder], ["Editable benchmarks & role tracks", cfg?.editable_benchmarks], ["Engagement insights", cfg?.engagement_insights], ["Weekly investor digest", cfg?.weekly_digest]].filter(([, on]) => on).map(([label]) => (
              <span key={label} className="flex items-center gap-1.5 font-medium text-slate-700">
                  <Check className="h-4 w-4 text-brand-ink" /> {label}
              </span>
            ))}
          </div>
          <SubscriptionControls sub={sub} planName={cfg?.name || billing.plan} onChanged={changed} />
        </Card>
      )}

      {billing && sub?.has_subscription && ["active", "trialing"].includes(sub.status) && sub?.interval === "month" && !sub?.cancel_at_period_end && (
        <AnnualNudge plan={billing.plan} onChanged={changed} />
      )}

      <BillingHistory history={history} error={historyError} onRetry={loadHistory} />

      {billing && billing.plan === "starter" && billing.plan_status !== "comped" && (
        <Card className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border p-5" data-testid="billing-redeem-card">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary"><Gift className="h-5 w-5 text-primary-foreground" /></div>
            <div>
              <div className="font-semibold text-foreground">Have an access code?</div>
              <p className="text-sm text-muted-foreground">Redeem it to unlock a paid plan for free.</p>
            </div>
          </div>
          <RedeemCodeInline onDone={changed} />
        </Card>
      )}

      {billing?.plan_status === "comped" ? (
        <Card className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-6" data-testid="comp-note-card">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-600"><Gift className="h-5 w-5 text-white" /></div>
            <div>
              <div className="h-display text-lg font-semibold text-foreground">You're on complimentary {cfg?.name} access</div>
              <p className="mt-1 text-sm text-muted-foreground">Every {cfg?.name} feature is unlocked, courtesy of the TenX team - with no card on file and no expiry. Nothing more to do.</p>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-2">
                <Zap className="h-4 w-4 text-brand-ink" />
            <h2 className="h-display text-lg font-semibold text-foreground">Change your plan</h2>
          </div>
          {!loading && !error && <PricingTiers subscription={sub} />}
        </>
      )}
      </>}
    </AppShell>
  );
}
